import React from 'react';
import { ButtonLink, InlineLink, ResourceLink } from '../shared/AcademicLink';

const MSEarlyEd = () => {
  const coreCourses = [
    { code: 'EDU 501', name: 'Review of Early Childhood Care and Education Principles and Theories', credits: 3 },
    { code: 'EDU 502', name: 'School Administration', credits: 3 },
    { code: 'EDU 525', name: 'Understanding Child Development', credits: 3 },
    { code: 'EDU 517', name: 'Socialization and Support', credits: 3 },
    { code: 'EDU 520', name: 'Early Childhood Care and Education Programs', credits: 3 },
    { code: 'EDU 522', name: 'Leadership, School, and the Community', credits: 3 },
    { code: 'EDU 523', name: 'School and the Curriculum', credits: 3 }
  ];

  const electiveCourses = [
    { code: 'EDU 524', name: 'Educational Research 1', credits: 3 },
    { code: 'EDU 531', name: 'Educational Research 2', credits: 3 },
    { code: 'EDU 567', name: 'Law and Education', credits: 3 },
    { code: 'EDU 601', name: 'Educational Theory, Research, and Practice', credits: 3 },
    { code: 'EDU 615', name: 'Leadership and Management 1', credits: 3 },
    { code: 'EDU 618', name: 'Leadership and Management 2', credits: 3 },
    { code: 'EDU 688', name: 'Data Analysis & School Administration', credits: 3 }
  ];

  const programFeatures = [
    {
      title: "Advanced Theory",
      description: "Deep understanding of early childhood education principles and contemporary theories",
      icon: "📚"
    },
    {
      title: "Leadership Skills",
      description: "Develop administrative and leadership capabilities for educational settings",
      icon: "👥"
    },
    {
      title: "Research Focus",
      description: "Strong emphasis on educational research and data analysis",
      icon: "🔬"
    },
    {
      title: "Community Impact",
      description: "Learn to build strong relationships between schools and communities",
      icon: "🌟"
    }
  ];

  const careerOpportunities = [
    {
      title: "School Administration",
      description: "Lead early childhood education programs and centers",
      icon: "🏫"
    },
    {
      title: "Program Director",
      description: "Design and oversee educational programs",
      icon: "📋"
    },
    {
      title: "Education Consultant",
      description: "Provide expert guidance to schools and organizations",
      icon: "💡"
    },
    {
      title: "Curriculum Specialist",
      description: "Develop and implement innovative educational curricula",
      icon: "📝"
    }
  ];

  const resources = [
    {
      title: "Course Descriptions",
      description: "Detailed information about each course in the program",
      path: "/academics/ms_early_ed/courses"
    },
    {
      title: "Research Opportunities",
      description: "Learn about current research projects and opportunities",
      path: "/academics/ms_early_ed/research"
    },
    {
      title: "Career Development",
      description: "Resources for professional growth and career advancement",
      path: "/academics/ms_early_ed/careers"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl font-bold mb-4">
              Master of Science in Early Childhood Education
            </h1>
            <p className="text-xl opacity-90">
              Advanced studies in early childhood development and educational leadership
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Overview Section */}
          <section className="mb-16">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6">Program Overview</h2>
              <p className="text-gray-600 mb-4">
                Our Master of Science in Early Childhood Education program prepares educators for 
                leadership roles in early childhood education. Learn more about our{' '}
                <InlineLink to="/academics/ms_early_ed/approach">educational approach</InlineLink> and{' '}
                <InlineLink to="/academics/ms_early_ed/research">research opportunities</InlineLink>.
              </p>
            </div>
          </section>

          {/* Program Features */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Program Features</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {programFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-8 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-2 h-full bg-primary-500"></div>
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">{feature.icon}</span>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-primary-600">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Core Courses */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Core Courses (21 credits)</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {coreCourses.map((course, index) => (
                  <div key={index} className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {course.code}: {course.name}
                        </h3>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                        {course.credits} Credits
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Elective Courses */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Elective Courses (Select 3 courses - 9 credits)</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {electiveCourses.map((course, index) => (
                  <div key={index} className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {course.code}: {course.name}
                        </h3>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                        {course.credits} Credits
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Career Opportunities */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Career Opportunities</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {careerOpportunities.map((career, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-8 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-2 h-full bg-primary-500"></div>
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">{career.icon}</span>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-primary-600">
                        {career.title}
                      </h3>
                      <p className="text-gray-600">{career.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Resources Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Program Resources</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {resources.map((resource, index) => (
                  <ResourceLink
                    key={index}
                    to={resource.path}
                    title={resource.title}
                    description={resource.description}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section>
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Advance Your Career in Education</h2>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                Take the next step in your educational career with our MS in Early Childhood Education program.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <ButtonLink to="/application" primary>
                  Apply Now
                </ButtonLink>
                <ButtonLink to="/request" primary={false}>
                  Request Information
                </ButtonLink>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default MSEarlyEd; 